import { Injectable } from '@angular/core';
import { HttpInterceptorService } from '../http-interceptor/http-interceptor.service';
import { TableOptions } from '../../classes/table-options';
import { DialogService } from '../dialog/dialog.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UserSearchDialogComponent } from 'src/app/components/admin/users/user-search/user-search-dialog/user-search-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { LimitReachedComponent } from '../../components/admin/users/access-cards/access-card/limit-reached/limit-reached.component';
import { UserType } from '../../user-roles';
import { RolesService } from '../roles/roles.service';
import { ResetPasswordPromptComponent } from '../../components/admin/users/reset-password-prompt/reset-password-prompt.component';
import { HttpParams } from '@angular/common/http';
import { ToastService } from '../../shared/services/toast/toast.service';

@Injectable()
export class UsersService {
  user: any;
  users: any = [];

  constructor(
    private _http: HttpInterceptorService,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private rolesService: RolesService,
    private toastService: ToastService,
    private translate: TranslateService
  ) {}

  getCustomers(options: TableOptions = new TableOptions()): Promise<any> {
    return new Promise((resolve, reject) => {
      let limit = options.limit;
      let startIndex = options.limit * options.offset;
      let sortType = options.prop;
      let sortDirection = options.dir === 'asc' ? 'Ascending' : 'Descending';
      let search = options.search || '';

      const params = new HttpParams({
        fromObject: {
          SortType: sortType,
          SortDirection: sortDirection,
          Search: search,
          StartIndex: startIndex,
          Take: limit
        }
      });

      this._http.get('Customers/GetCustomers', { params }).subscribe({
        next: (users) => {
          resolve(users);
        },
        error: (err) => {
          reject(err);
        }
      });
    });
  }
  /**
   * Opens search dialog to select customer
   */
  selectCustomer(): Promise<any> {
    return new Promise((resolve) => {
      let dialogRef: MatDialogRef<UserSearchDialogComponent>;

      dialogRef = this.dialog.open(UserSearchDialogComponent);

      dialogRef.afterClosed().subscribe((customer) => {
        resolve(customer);
      });
    });
  }
  select(user) {
    this.user = user;
  }
  getUser(id, reload?): Promise<any> {
    return new Promise((resolve) => {
      if (this.user && !reload && this.user.PK_CustomerID === id) {
        resolve(this.user);
      } else {
        this._http.get('Customers/' + id).subscribe(
          (user) => {
            resolve(user);
          },
          (err) => {
            resolve({});
          }
        );
      }
    });
  }
  updateCustomer(customer): Promise<any> {
    return new Promise((resolve, reject) => {
      this._http.put('Customers', customer).subscribe(
        (newCustomer) => {
          resolve(newCustomer);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  updateUser(user): Promise<any> {
    return new Promise((resolve, reject) => {
      this._http.put('Users', user).subscribe(
        (newUser) => {
          resolve(newUser);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }
  updateEmail(email, userID): Promise<any> {
    return new Promise((resolve) => {
      this._http.post('Users/UpdateEmailAddress', { Email: email, UserID: userID }).subscribe(
        (res) => {
          resolve(res.success);
        },
        (err) => {
          resolve(false);
        }
      );
    });
  }
  searchUser(email = '', phone = '', countryindicator = ''): Promise<any> {
    return new Promise((resolve) => {
      if (!email && !phone && !countryindicator) {
        resolve(false);
      } else {
        const params = new HttpParams({
          fromObject: { email: email.trim(), phone: phone.trim(), countryindicator: countryindicator.trim() }
        });

        this._http.get('Customers/Search', { params }).subscribe({
          next: (user) => {
            if (user.length) {
              resolve(user[0]);
            } else {
              this.toastService.error('USER_NOT_FOUND');
              resolve(false);
            }
          },
          error: (err) => {
            this.toastService.error(err);
            resolve(false);
          }
        });
      }
    });
  }
  toggleBlock(customerID, isBlocked: boolean) {
    return this._http.patch(`Customers/${customerID}/block/${isBlocked}`, null);
  }
  checklist(userID): Promise<any> {
    return new Promise((resolve) => {
      this._http.get('Users/checklist/' + userID).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          resolve(null);
        }
      );
    });
  }
  checkUnpaid(customerID): Promise<any> {
    return new Promise((resolve) => {
      this._http.get('Invoices/UnpaidInvoices/' + customerID).subscribe(
        (res) => {
          if (res.length) {
            resolve(res);
          } else {
            resolve([]);
          }
        },
        (err) => {
          resolve([]);
        }
      );
    });
  }
  getPreferences(userID): Promise<any> {
    return new Promise((resolve) => {
      this._http.get('UserPreferences/GetUserPreferences/' + userID).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          resolve({});
        }
      );
    });
  }
  savePreferenses(preferences): Promise<any> {
    return new Promise((resolve) => {
      this._http.put('UserPreferences', preferences).subscribe(
        (res) => {
          this.toastService.success('PREFERENCES_UPDATED');
          resolve(true);
        },
        (err) => {
          this.toastService.error(err);
          resolve(false);
        }
      );
    });
  }
  getActiveSessions(userID): Promise<any> {
    return new Promise((resolve) => {
      this._http.get(`sessions/user/${userID}/active`).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          resolve([]);
        }
      );
    });
  }
  getAccessCards(customerID): Promise<any> {
    return new Promise((resolve) => {
      this._http.get('AccessCards/ByCustomer/' + customerID).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          resolve([]);
        }
      );
    });
  }
  addAccessCard(card): Promise<any> {
    return new Promise((resolve) => {
      this._http.post('AccessCards', card).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          console.log(err);

          if (err.errorName === 'rfidAlreadyExists') {
            this.toastService.error('RFID_ALREADY_EXISTS');
            resolve(false);
          } else if (
            err.status === 400 &&
            err.errorName === 'End user is not allowed to have more access cards.' &&
            this.rolesService.hasHighlevelAccess(UserType.Admin)
          ) {
            const dialogRef = this.dialog.open(LimitReachedComponent);
            dialogRef.afterClosed().subscribe((res) => {
              if (res === true) {
                this.addAccessCard({ ...card, OverrideLimitOfRFIDs: true }).then((res) => {
                  resolve(res);
                });
              } else {
                resolve(false);
              }
            });
          } else if (err.errorName === '500 Internal Server Error') {
            //FIX: We only do this because backend devs are working slow.
            resolve(true);
          } else {
            this.toastService.error(err);
            resolve(false);
          }
        }
      );
    });
  }
  updateAccessCard(card): Promise<any> {
    return new Promise((resolve) => {
      this._http.put('AccessCards', card).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => {
          if (err.errorName === 'rfidAlreadyExists') {
            this.toastService.error('RFID_ALREADY_EXISTS');
          } else {
            this.toastService.error(err);
          }
          resolve(false);
        }
      );
    });
  }
  deleteAccessCard(cardID): Promise<any> {
    return new Promise((resolve) => {
      this.dialogService
        .prompt(this.translate.instant('CONFIRM_DELETE_CARD'), null, this.translate.instant('DELETE'))
        .subscribe((ok) => {
          if (ok) {
            this._http.delete('AccessCards/' + cardID).subscribe(
              (res) => {
                this.toastService.success('CARD_DELETED');
                resolve(true);
              },
              (err) => {
                this.toastService.error(err);
                resolve(false);
              }
            );
          } else {
            resolve(false);
          }
        });
    });
  }
  generateInvoices(userID): Promise<any> {
    return new Promise((resolve) => {
      this.dialogService
        .prompt(this.translate.instant('CONFIRM_GENERATE_INVOICES'), null, this.translate.instant('GENERATE'))
        .subscribe((ok) => {
          if (ok) {
            this._http.get('Invoices/GenerateInvoicesForAUser/' + userID).subscribe(
              (res) => {
                this.toastService.success('INVOICES_GENERATED');
                resolve(true);
              },
              (err) => {
                this.toastService.error(err);
                resolve(false);
              }
            );
          } else {
            resolve(false);
          }
        });
    });
  }
  resetPassword(userID): Promise<any> {
    return new Promise((resolve) => {
      const dialogRef = this.dialog.open(ResetPasswordPromptComponent, {
        width: '400px'
      });

      dialogRef.componentRef.setInput('userId', userID);

      dialogRef.afterClosed().subscribe((res) => {
        resolve(false);
      });
    });
  }
  createNewPassword(userID): Promise<any> {
    return new Promise((resolve, reject) => {
      let suggestedPass = Math.random().toString(36).substr(2, 10);
      this.dialogService
        .input(
          this.translate.instant('SET_NEW_PASSWORD'),
          this.translate.instant('NEW_PASSWORD'),
          this.translate.instant('SET'),
          null,
          null,
          suggestedPass
        )
        .subscribe((newPass) => {
          if (newPass) {
            this._http
              .post('Users/SetPasswordAdmin', {
                PK_UserID: userID,
                NewPassword: newPass
              })
              .subscribe(
                (res) => {
                  if (res.success) {
                    resolve(true);
                  } else {
                    reject('Unknown error');
                  }
                },
                (err) => {
                  reject(err.errorName);
                }
              );
          } else {
            resolve(false);
          }
        });
    });
  }
  getPaymentMethods(customerID, getInactive = false): Promise<any> {
    return new Promise((resolve, reject) => {
      this._http.get('PaymentMethods/' + customerID + '/' + getInactive).subscribe(
        (res) => {
          resolve(res);
        },
        () => {
          reject();
        }
      );
    });
  }
  deleteUser(userID): Promise<boolean> {
    return new Promise((resolve) => {
      this.dialogService
        .prompt(this.translate.instant('CONFIRM_DELETE_USER'), null, this.translate.instant('DELETE'))
        .subscribe((ok) => {
          if (ok) {
            this._http.delete('Users/' + userID).subscribe(
              (res) => {
                this.toastService.success('USER_DELETED');
                resolve(true);
              },
              (err) => {
                this.toastService.error(err);
                resolve(false);
              }
            );
          }
        });
    });
  }
  deletePaymentMethod(methodID, showSuccessMsg = true): Promise<boolean> {
    return new Promise((resolve) => {
      this._http.delete('PaymentMethods/' + methodID).subscribe(
        (res) => {
          if (showSuccessMsg) {
            this.toastService.success('METHOD_DELETED');
          }
          resolve(true);
        },
        (err) => {
          this.toastService.error(err);
          resolve(false);
        }
      );
    });
  }
  setDefaultPaymentMethod(methodID): Promise<any> {
    return new Promise((resolve) => {
      this._http.put('PaymentMethods/SetDefault/' + methodID, {}).subscribe(
        (res) => {
          this.toastService.success('DEFAULT_PAYMENT_SET');
          resolve(res);
        },
        (err) => {
          this.toastService.error(err);
          resolve(false);
        }
      );
    });
  }

  getSupportedLanguages() {
    return this._http.get('Users/GetSupportedLanguages');
  }
  getTimeZones() {
    return this._http.get('UserPreferences/GetTimezones');
  }

  getCustomerByVehicle(vehicleIdentifier: string) {
    const params = new HttpParams({
      fromObject: {
        vehicleIdentifier
      }
    });

    return this._http.get(`Customers/by-vehicle`, { params });
  }
}
